import type {
  ProductUpdateCategory,
  ProductUpdatePlatform,
  SanityProductUpdateSummary
} from '@lib/sanity/types';

/** The newest updates get a large card each; the rest sit in a grid below. */
export const FEATURED_UPDATE_COUNT = 3;

export const CATEGORY_LABELS: Record<ProductUpdateCategory, string> = {
  newFeature: 'New feature',
  improvement: 'Improvement',
  spotlight: 'Spotlight',
  beta: 'Beta',
  comingSoon: 'Coming soon'
};

export const PLATFORM_LABELS: Record<ProductUpdatePlatform, string> = {
  app: 'In the app',
  website: 'On the website',
  both: 'App and website'
};

// Phone screenshots run about 2.2 times taller than wide; a tablet or desktop
// screenshot is nowhere near, so anything this tall is framed as a phone.
const PHONE_SCREENSHOT_MIN_HEIGHT_RATIO = 1.7;

export const isPhoneScreenshot = (
  dimensions: { width?: number; height?: number } | undefined
): boolean =>
  Boolean(
    dimensions?.width &&
      dimensions.height &&
      dimensions.height / dimensions.width >= PHONE_SCREENSHOT_MIN_HEIGHT_RATIO
  );

export const updateDate = (update: SanityProductUpdateSummary): string =>
  update.publishedAt ?? update._createdAt;

const byDateNewestFirst = (
  first: SanityProductUpdateSummary,
  second: SanityProductUpdateSummary
): number => updateDate(second).localeCompare(updateDate(first));

export const splitFeaturedUpdates = (
  updates: SanityProductUpdateSummary[]
): {
  featured: SanityProductUpdateSummary[];
  earlier: SanityProductUpdateSummary[];
} => {
  const ordered = [...updates].sort(byDateNewestFirst);
  return {
    featured: ordered.slice(0, FEATURED_UPDATE_COUNT),
    earlier: ordered.slice(FEATURED_UPDATE_COUNT)
  };
};

export type NeighbourUpdate = { slug: string; title: string; date: string };

const toNeighbour = (
  update: SanityProductUpdateSummary | undefined
): NeighbourUpdate | null =>
  update
    ? { slug: update.slug, title: update.title, date: updateDate(update) }
    : null;

/** The updates published either side of this one, newest first as listed. */
export const findNeighbours = (
  updates: SanityProductUpdateSummary[],
  slug: string
): { newer: NeighbourUpdate | null; older: NeighbourUpdate | null } => {
  const ordered = [...updates].sort(byDateNewestFirst);
  const index = ordered.findIndex((update) => update.slug === slug);
  if (index === -1) return { newer: null, older: null };
  return {
    newer: toNeighbour(ordered[index - 1]),
    older: toNeighbour(ordered[index + 1])
  };
};
